import React from 'react'
import PropTypes from 'prop-types'

import useSetState from 'hooks/useSetState'

import Box from 'suitTheme/Box'
import FormField from 'components/forms/FormField'
import Grid from 'suitTheme/Grid'
import ImageUppyUploadZone from 'suitTheme/ImageUppyUploadZone'
import Link from 'suitTheme/Link'
import SidebarModal from 'modals/sidebar/SidebarModal'

import useImageSelect from './hooks/useImageSelect'

import LibrarySelector from './components/LibrarySelector'
import SpecifiedImages from './components/SpecifiedImages'
import Thumbnail from './components/Thumbnail'

const tileSizes = {
  large: {
    width: 320,
    height: 200,
  },
  small: {
    width: 160,
    height: 110,
  },
}

const defaultState = {
  viewMode: 'small',
}

const ImageSelectModal = (props) => {
  const {
    callbacks,
    callbacks: { selectImage },
    creating,
    modalKey,
    preselectedEntity,
    showModal,
    specifiedImageIds,
  } = props

  const [state, setState] = useSetState(defaultState)
  const { viewMode } = state

  const imageSelectPayload = useImageSelect({ preselectedEntity, specifiedImageIds })
  const {
    callbacks: { setState: setImageSelectState },
    filteredImages,
    hasImages,
    hasSpecifiedImages,
    loading,
    selectedEntity,
    selectedEntityId,
    selectedEntityType,
    specifiedImages,
  } = imageSelectPayload

  const tileSize = tileSizes[viewMode] || {}

  return (
    <SidebarModal callbacks={callbacks} modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Select" titleSecondLine="Image" />

      <SidebarModal.Content hasFooter={false}>
        <Box flexDirection="column">
          {hasSpecifiedImages && (
            <SpecifiedImages
              callbacks={callbacks}
              specifiedImages={specifiedImages}
              tileSize={tileSize}
              viewMode={viewMode}
            />
          )}

          <FormField direction="column" label="Image Source">
            <select
              onChange={e => setImageSelectState({ selectedEntityId: 0, selectedEntityType: e.target.value })}
              value={selectedEntityType}
            >
              <option value="User">Agents</option>
              <option value="Campaign">Campaigns</option>
              <option value="CampaignComparable">Campaign Comparables</option>
              <option value="ImageLibrary">Image Libraries</option>
            </select>
          </FormField>

          <LibrarySelector imageSelectPayload={imageSelectPayload} />

          {selectedEntityId !== 0 && selectedEntityType && (
            <FormField
              direction="column"
              label="Images"
              labelRight={(
                <Link
                  onClick={() => setState({ viewMode: viewMode === 'large' ? 'small' : 'large' })}
                  textProps={{ fontSize: 'xsmall' }}
                >
                  {viewMode === 'large' ? 'View Small Tiles' : 'View Large Tiles'}
                </Link>
              )}
              style={{ marginTop: 16 }}
            >
              {hasImages && !loading && !!selectedEntity.id && (
                <Grid
                  gridGap="medium"
                  gridAutoRows="1fr"
                  gridTemplateColumns={`repeat(auto-fill, minmax(${tileSize.width}px, 1fr))`}
                  gridTemplateRows={`repeat(auto-fill, minmax(${tileSize.height}px, 1fr))`}
                >
                  {filteredImages.map(image => (
                    <Thumbnail
                      callbacks={{ selectImage }}
                      key={image.id}
                      image={image}
                      viewMode={viewMode}
                      creating={creating}
                    />
                  ))}
                </Grid>
              )}

              {selectedEntity.owner_type !== 'Brand' && (
                <ImageUppyUploadZone
                  endpoint="/agency/images.json"
                  imageType="photo"
                  imageableId={selectedEntityId || 0}
                  imageableType={selectedEntityType || 'Campaign'}
                  requestOptions={{ v2: true, include_image_sizes: true }}
                  style={{ marginTop: 8 }}
                />
              )}
            </FormField>
          )}
        </Box>
      </SidebarModal.Content>
    </SidebarModal>
  )
}

ImageSelectModal.propTypes = {
  callbacks: PropTypes.shape({
    closeModal: PropTypes.func.isRequired,
    selectImage: PropTypes.func.isRequired,
    setState: PropTypes.func.isRequired,
  }).isRequired,
  creating: PropTypes.bool,
  modalKey: PropTypes.string,
  preselectedEntity: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.string,
  }),
  showModal: PropTypes.bool,
  specifiedImageIds: PropTypes.array,
}

ImageSelectModal.defaultProps = {
  modalKey: 'ImageSelectModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <ImageSelectModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
